import {
  User as AccountEntity,
  ApiEndpoints,
  EntityType,
  UserRoleAssignmentsByEntity,
} from 'adc-sirius-module-common';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { urls } from '../utilities/dataHelpers';
import {
  Account,
  AccountRoleAssignmentsUpdateRequest,
  AccountWithRoleAssignments,
} from '../models';
import {
  mapAccountEntityToAccount,
  mapAccountRoleAssignmentsUpdateRequestToUserRoleAssignmentsByEntityUpdate,
  mapAccountToAccountEntity,
  mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments,
} from '../utilities/mappers';

const handleGetAccounts = (request: MediatorRequestWithResponse<Account[]>) => {
  request.response = new Promise<Account[]>((resolve, reject) => {
    mediator
      .request<AccountEntity[]>('api:invoke', {
        method: ApiEndpoints.EP_USER_GET.Method,
        url: urls.api + ApiEndpoints.EP_USER_GET.Path,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(result.data.map(mapAccountEntityToAccount));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleGetAccount = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: ApiEndpoints.EP_USER_GET_BY_KEY.Method,
        url: `${urls.api}${ApiEndpoints.EP_USER_GET_BY_KEY.Path.replace(':key', request.key)}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleCreateAccount = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<{ user: AccountEntity }>('api:invoke', {
        method: ApiEndpoints.EP_AUTH_REGISTER.Method,
        url: urls.api + ApiEndpoints.EP_AUTH_REGISTER.Path,
        data: {
          firstName: request.data.firstName,
          lastName: request.data.lastName,
          email: request.data.email,
          password: request.data.password,
        },
      })
      .then((result) => {
        if (result.status === 201) {
          resolve(mapAccountEntityToAccount(result.data.user));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleUpdateAccount = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: ApiEndpoints.EP_USER_UPDATE.Method,
        url: urls.api + ApiEndpoints.EP_USER_UPDATE.Path,
        data: mapAccountToAccountEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleInviteUser = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/invite`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleResetPassword = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/reset-password`,
        data: request.data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleAccountLock = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/lock`,
        data: request.data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleAccountUnlock = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/unlock`,
        data: request.data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleGetAccountsByEntity = (
  request: MediatorRequestWithResponse<AccountWithRoleAssignments[]> & {
    key: string;
    entityType: EntityType;
  },
) => {
  const url =
    urls.api +
    ApiEndpoints.EP_USER_GET_BY_ENTITY.Path.replace(
      ':key',
      request.key,
    ).replace(':entityType', request.entityType.toLowerCase());

  request.response = new Promise<AccountWithRoleAssignments[]>(
    (resolve, reject) => {
      mediator
        .request<UserRoleAssignmentsByEntity>('api:invoke', {
          method: 'GET',
          url,
          entityType: request.entityType,
        })
        .then((result) => {
          if (result.status === 200) {
            const mappedAccounts =
              mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments([
                result.data,
              ]);

            resolve(mappedAccounts);
          } else {
            reject(result);
          }
        })
        .catch((err) => {
          reject(err);
        });
    },
  );
};

const handleUpdateAccountRoles = (
  request: MediatorRequestWithResponse<AccountWithRoleAssignments[]> & {
    data: AccountRoleAssignmentsUpdateRequest;
  },
) => {
  const { data } = request;

  const url = urls.api + ApiEndpoints.EP_USER_UPDATE_ROLES.Path;

  request.response = new Promise<AccountWithRoleAssignments[]>(
    (resolve, reject) => {
      mediator
        .request<UserRoleAssignmentsByEntity>('api:invoke', {
          method: ApiEndpoints.EP_USER_UPDATE_ROLES.Method,
          url,
          data: mapAccountRoleAssignmentsUpdateRequestToUserRoleAssignmentsByEntityUpdate(
            data,
          ),
        })
        .then((result) => {
          if (result.status === 200) {
            resolve(
              mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments([
                result.data,
              ]),
            );
          } else {
            reject(result);
          }
        })
        .catch((err) => {
          reject(err);
        });
    },
  );
};

mediator.subscribe('account:getAccounts', handleGetAccounts);
mediator.subscribe('account:getAccount', handleGetAccount);
mediator.subscribe('account:createAccount', handleCreateAccount);
mediator.subscribe('account:updateAccount', handleUpdateAccount);
mediator.subscribe('account:sendInvite', handleInviteUser);
mediator.subscribe('account:resetPassword', handleResetPassword);
mediator.subscribe('account:lock', handleAccountLock);
mediator.subscribe('account:unlock', handleAccountUnlock);
mediator.subscribe('account:getAccountsByEntity', handleGetAccountsByEntity);
mediator.subscribe('account:updateAccountRoles', handleUpdateAccountRoles);
